import {
  Autocomplete,
  Chip,
  CircularProgress,
  TextField,
  TextFieldProps,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { IdNameModel } from "services/KnowfullyAdminClient";

type SelectNotificationChannelBaseProps = {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  textFieldProps?: Partial<TextFieldProps>;
};

type SelectNotificationChannelMultiProps = SelectNotificationChannelBaseProps & {
  multiple?: true;
  value: string[];
  onChange: (ids: string[]) => void;
};

type SelectNotificationChannelSingleProps = SelectNotificationChannelBaseProps & {
  multiple: false;
  value: string;
  onChange: (id: string) => void;
};

type SelectNotificationChannelProps =
  | SelectNotificationChannelMultiProps
  | SelectNotificationChannelSingleProps;

const SelectNotificationChannel = ({
  value,
  onChange,
  label,
  disabled,
  required,
  textFieldProps,
  multiple,
}: SelectNotificationChannelProps) => {
  const { formatMessage: t } = useIntl();
  const { notificationsConfigurationsService } = useGlobalContext();

  const isSingle = multiple === false;

  const { data, isLoading } = useQuery(
    ["notificationsConfigurationsService.getNotificationRoutingTypes"],
    () => notificationsConfigurationsService.getNotificationRoutingTypes(),
  );

  const options = data?.data ?? [];

  const selectedValues = isSingle
    ? options.find((o) => o.id === value) ?? null
    : options.filter((o) => (value as string[]).includes(o.id!));

  return (
    <Autocomplete
      multiple={!isSingle}
      autoComplete
      disableCloseOnSelect={!isSingle}
      limitTags={isSingle ? undefined : 3}
      loading={isLoading}
      disabled={disabled || isLoading}
      options={options}
      value={selectedValues}
      getOptionLabel={(option) => (option as IdNameModel).name || ""}
      isOptionEqualToValue={(option, val) =>
        option.id === (val as IdNameModel).id
      }
      onChange={(_event, newValue) => {
        if (isSingle) {
          (onChange as (id: string) => void)(
            (newValue as IdNameModel | null)?.id ?? "",
          );
        } else {
          (onChange as (ids: string[]) => void)(
            (newValue as IdNameModel[]).map((v) => v.id!).filter(Boolean),
          );
        }
      }}
      renderTags={
        isSingle
          ? undefined
          : (tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  size="small"
                  label={(option as IdNameModel).name}
                  {...getTagProps({ index })}
                  key={(option as IdNameModel).id}
                />
              ))
      }
      renderInput={(input) => (
        <TextField
          variant="outlined"
          {...textFieldProps}
          {...input}
          label={label || t({ id: "channel" })}
          required={required}
          InputProps={{
            ...textFieldProps?.InputProps,
            ...input.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {input.InputProps.endAdornment}
              </>
            ),
          }}
          fullWidth
        />
      )}
    />
  );
};

export default SelectNotificationChannel;
